/**
 * Hook de Gerenciamento de Estado do Salvamento
 * 
 * Gerencia os estados principais da UI e controle:
 * - saveState: Estado do salvamento (saving, uploading, success, error, etc)
 * - errorMessage: Mensagem de erro atual
 * - isClosing: Flag de fechamento do modal
 * - needConfirmUpdate: Flag de confirmação de atualização
 * 
 * Origem: linhas 33, 45-47 do useSaveController.ts
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import type { SaveState } from '../types';

/**
 * Opções do hook useSaveState
 */
export interface UseSaveStateOptions {
  /**
   * Estado inicial do salvamento
   * @default 'saving'
   */
  initialState?: SaveState;
}

/**
 * Retorno do hook useSaveState
 */
export interface UseSaveStateReturn {
  // Estados
  /**
   * Estado atual do salvamento
   */
  saveState: SaveState;
  
  /**
   * Mensagem de erro atual (vazia se sem erro)
   */
  errorMessage: string;
  
  /**
   * Se o modal está fechando
   */
  isClosing: boolean;
  
  /**
   * Se precisa confirmação de atualização (conflito 409)
   */
  needConfirmUpdate: boolean;
  
  // Actions
  /**
   * Define o estado do salvamento
   */
  setSaveState: (state: SaveState) => void;
  
  /**
   * Define mensagem de erro e muda estado para 'error'
   */
  setErrorMessage: (message: string) => void;
  
  /**
   * Define flag de fechamento
   */
  setIsClosing: (closing: boolean) => void;
  
  /**
   * Marca sucesso (limpa erro, estado = success)
   */
  setSuccess: () => void;
  
  /**
   * Marca erro com mensagem
   */
  setError: (message: string) => void;
  
  /**
   * Limpa mensagem de erro
   */
  clearError: () => void;
  
  /**
   * Confirma atualização (resolve promise de confirmação)
   */
  confirmUpdate: () => void;
  
  /**
   * Cancela atualização (rejeita promise de confirmação)
   */
  cancelUpdate: () => void;
  
  /**
   * Mostra diálogo de confirmação (retorna promise)
   */
  showConfirmDialog: () => Promise<boolean>;
}

/**
 * Hook de gerenciamento de estado do salvamento
 * 
 * Centraliza todos os estados principais relacionados ao UI e controle.
 * 
 * Padrão de confirmação:
 * - showConfirmDialog() retorna Promise<boolean>
 * - UI chama confirmUpdate() ou cancelUpdate()
 * - Promise é resolvida/rejeitada
 * 
 * @param options - Opções do hook
 * @returns Estados e actions
 * 
 * @example
 * ```typescript
 * const saveState = useSaveState({ initialState: 'saving' });
 * 
 * // Marcar erro
 * saveState.setError('Falha ao salvar arquivo');
 * 
 * // Marcar sucesso
 * saveState.setSuccess();
 * 
 * // Confirmação
 * const confirmar = await saveState.showConfirmDialog();
 * if (confirmar) {
 *   // Usuário confirmou
 * }
 * ```
 */
export function useSaveState(options: UseSaveStateOptions = {}): UseSaveStateReturn {
  const { initialState = 'saving' } = options;

  // Estados principais
  const [saveState, setSaveState] = useState<SaveState>(initialState);
  const [errorMessage, setErrorMessageState] = useState<string>('');
  const [isClosing, setIsClosing] = useState(false);
  const [needConfirmUpdate, setNeedConfirmUpdate] = useState(false);

  // Ref para resolver promise de confirmação
  const confirmResolverRef = useRef<(value: boolean) => void>();

  /**
   * Define mensagem de erro e muda estado para 'error'
   */
  const setErrorMessage = useCallback((message: string) => {
    setErrorMessageState(message);
    if (message && saveState !== 'error') {
      setSaveState('error');
    }
  }, [saveState]);

  /**
   * Marca como sucesso
   */
  const setSuccess = useCallback(() => {
    setErrorMessageState('');
    setSaveState('success');
  }, []);

  /**
   * Marca como erro com mensagem
   */
  const setError = useCallback((message: string) => {
    setErrorMessageState(message);
    setSaveState('error');
  }, []);

  /**
   * Limpa mensagem de erro (sem mudar estado)
   */
  const clearError = useCallback(() => {
    setErrorMessageState('');
  }, []);

  /**
   * Confirma atualização
   * 
   * Resolve a promise criada por showConfirmDialog com true
   */
  const confirmUpdate = useCallback(() => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(true);
    }
    setNeedConfirmUpdate(false);
  }, []);

  /**
   * Cancela atualização
   * 
   * Resolve a promise criada por showConfirmDialog com false
   */
  const cancelUpdate = useCallback(() => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(false);
    }
    setNeedConfirmUpdate(false);
  }, []);

  /**
   * Mostra diálogo de confirmação de arquivo existente
   * 
   * Cria uma promise que será resolvida quando o usuário
   * chamar confirmUpdate() ou cancelUpdate()
   * 
   * @returns Promise que resolve com true (confirmar) ou false (cancelar)
   */
  const showConfirmDialog = useCallback((): Promise<boolean> => {
    setNeedConfirmUpdate(true);
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
    });
  }, []);

  // Memoizar retorno para evitar recriação
  return useMemo(() => ({
    // Estados
    saveState,
    errorMessage,
    isClosing,
    needConfirmUpdate,

    // Actions
    setSaveState,
    setErrorMessage,
    setIsClosing,
    setSuccess,
    setError,
    clearError,
    confirmUpdate,
    cancelUpdate,
    showConfirmDialog,
  }), [
    saveState,
    errorMessage,
    isClosing,
    needConfirmUpdate,
    setSaveState,
    setErrorMessage,
    setIsClosing,
    setSuccess,
    setError,
    clearError,
    confirmUpdate,
    cancelUpdate,
    showConfirmDialog,
  ]);
}

